// DJI keeps two vertical datums apart: the aircraft flies WGS84 ellipsoid
// heights, while 司空 2 / Pilot 2 edit and display EGM96 mean sea level
// altitudes (dji-wpml overview, 高程转换). Writing an ellipsoid height into a
// field the editors read as EGM96 lifts the route — and the reference takeoff
// point with it — by the geoid undulation, which is +11 m around Shanghai and
// −65 m in eastern Xinjiang. Converting needs the undulation N, where
//   ellipsoid height = EGM96 altitude + N.
//
// The EGM96 15′ grid is ~2.7 MB, so it loads on demand as its own chunk:
// await loadGeoidModel() before saving or importing a route, then use the
// synchronous lookups below.

interface GeoidModel {
  meanSeaLevel (latitude: number, longitude: number): number
}

let model: GeoidModel | null = null
let loading: Promise<GeoidModel> | null = null

export function loadGeoidModel (): Promise<GeoidModel> {
  if (!loading) {
    loading = import('egm96-universal').then(runtime => {
      if (typeof runtime.meanSeaLevel !== 'function') throw new Error('EGM96 模块缺少 meanSeaLevel。')
      model = runtime
      return runtime
    }).catch(() => {
      loading = null
      throw new Error('EGM96 高程基准数据加载失败，请检查网络后重试。')
    })
  }
  return loading
}

export function isGeoidModelLoaded (): boolean {
  return model !== null
}

/** Start loading without blocking; failures resurface on the awaited call. */
export function preloadGeoidModel (): void {
  loadGeoidModel().catch(() => {})
}

/** Geoid undulation N in metres, or null when the grid is not loaded yet. */
export function geoidUndulation (latitude: number, longitude: number): number | null {
  if (!model || !Number.isFinite(latitude) || !Number.isFinite(longitude)) return null
  if (latitude < -90 || latitude > 90 || longitude < -180 || longitude > 180) return null
  const undulation = model.meanSeaLevel(latitude, longitude)
  return Number.isFinite(undulation) ? undulation : null
}

/** WGS84 ellipsoid height to EGM96 altitude (what 司空 2 / Pilot 2 edit). */
export function ellipsoidToMeanSeaLevel (latitude: number, longitude: number, height: number): number | null {
  const undulation = geoidUndulation(latitude, longitude)
  return undulation === null || !Number.isFinite(height) ? null : height - undulation
}

/** EGM96 altitude to WGS84 ellipsoid height (what the aircraft flies). */
export function meanSeaLevelToEllipsoid (latitude: number, longitude: number, height: number): number | null {
  const undulation = geoidUndulation(latitude, longitude)
  return undulation === null || !Number.isFinite(height) ? null : height + undulation
}
